// @ts-ignore
import React from "react";
import { makeAutoObservable, action } from "mobx";
import { api } from "core/utility";
import _ from "lodash";
import axios, { rawAxios, cancelToken } from "core/config/axios";
import {
  INITIAL_MESSAGE_FONT_SIZE,
  InitialData,
  MESSAGE_CENTER_AUDIT_NOTIFICATION_ID,
  LoyalFanFiltersEnum,
  MESSAGES_PER_REQUEST,
  LoyalFanFiltersKey,
  ATTACHMENTS_LIST_PAGE_SIZE,
  MESSAGE_LIST_REFRESH_DELAY_IN_MS,
  MESSAGE_THUMBNAIL_REFRESH_MAX_ATTEMPTS,
  MC_CS_AGENT_PROFILE_IMG,
} from "./consts";
import {
  MessageAttachmentDetails,
  MessageBlockType,
  MessageCenterAuditWarning,
  MessageCenterContactDetails,
  MessageCenterConversationBlockedData,
  MessageCenterConversationDetails,
  MessageCenterMessageAttachmentDetails,
  MessageCenterMessageDetails,
  MessageCenterSystemMessageUser,
  MessageQueueChunk,
  MessageRequestData,
  MessageVideoAttachmentUploadedData,
  MessagesLoadMoreType,
  PendingAttachmentData,
  PendingMessage,
  SharedMediaTab,
} from "./types";
import { snackbarStore } from "library/core/stores/snackbar/SnackbarStore";
import { history, logger } from "library/core/utility";
import { FormattedMessage } from "react-intl";
import { format } from "date-fns";
import { SnackbarVariants } from "library/core/stores/snackbar/enums";
import { Notification } from "common/my-page/stores/profile/types";
import { modalStore } from "library/core/stores/modal";
import AccountAuditedDisclaimer from "common/account-audited-disclaimer";
import { authStore, profileStore, stores } from "core/stores";
import { createFormData, createCustomFormData } from "core/utility/misc";
import config from "core/config";
import { dateProcess } from "library/core/utility";
import CookieStorage from "library/core/utility/cookie-storage";
import { SharedMedia } from "common/messages/shared-media-modal/types";
import { TFontSize } from "library/styles/tailwind-classnames";
import { CancelTokenSource } from "axios";
import sanitizeHtml from "sanitize-html";
import { exponentialBackoffWithJitter } from "library/core/utility/misc";
import { SharedMediaTypeEnum } from "./enum";

const logPrefix = "[MessageStore]";

export default class MessageStore {
  isComposing: boolean = InitialData.isComposing;
  isSearchingView: boolean = InitialData.isSearchingView;
  isDeleting: boolean = InitialData.isDeleting;
  isBulkMessageView: boolean = InitialData.isBulkMessageView;
  isSendingMessage: boolean = false;

  message: string = InitialData.message;
  messageCharCount: number = InitialData.messageCharCount;
  unreadConversationMessagesCount: number =
    InitialData.unreadConversationMessagesCount;
  unreadNewsAndPolicyCount: number = InitialData.unreadNewsAndPolicyCount;

  contacts: MessageCenterContactDetails[] = InitialData.contacts;
  csContacts: MessageCenterContactDetails[] = InitialData.csContacts;
  unFilteredContacts: MessageCenterContactDetails[] = InitialData.contacts;
  searchedContacts: MessageCenterContactDetails[] = InitialData.contacts;
  selectedContacts: string[] = InitialData.selectedContacts;
  nextContactsUrl: string = InitialData.nextContactsUrl;
  isContactsLoading: boolean = InitialData.isContactsLoading;
  isContactsLoadingMore: boolean = InitialData.isContactsLoadingMore;
  isSearchingContacts: boolean = InitialData.isSearchingContacts;
  contactsSearchTerm: string = InitialData.contactsSearchTerm;
  contactsSearchTermReaction: any = InitialData.contactsSearchTermReaction;
  contactsFilterTypes: string[] = InitialData.contactsFilterTypes;
  currentContactChain: number = 0;
  activeConversation: MessageCenterConversationDetails | null =
    InitialData.activeConversation;
  activeSearchedWord: string = InitialData.activeSearchedWord;

  newsAndPolicies: MessageCenterConversationDetails[] =
    InitialData.newsAndPolicies;
  activeNewsAndPolicy: MessageCenterConversationDetails | null =
    InitialData.activeNewsAndPolicy;
  systemMessageUser: MessageCenterSystemMessageUser | null =
    InitialData.systemMessageUser;
  systemMessageUserIds: string[] = [];

  conversations: MessageCenterConversationDetails[] = InitialData.conversations;
  csConversation: MessageCenterConversationDetails | null =
    InitialData.csConversation;
  selectedConversations: string[] = InitialData.selectedConversations;
  activeConversationMessages: MessageCenterMessageDetails[] =
    InitialData.activeConversationMessages;
  nextConversationsUrl: string = InitialData.nextConversationsUrl;
  isConversationsLoading: boolean = InitialData.isConversationsLoading;
  isConversationsLoadingMore: boolean = InitialData.isConversationsLoadingMore;
  conversationsSearchTerm: string = InitialData.conversationsSearchTerm;
  isSearchingConversations: boolean = InitialData.isSearchingConversations;
  conversationsSearchTermReaction: any =
    InitialData.conversationsSearchTermReaction;
  searchingMessagesStartDate: string = InitialData.searchingMessagesStartDate;
  isActiveConversationBlocked: boolean =
    InitialData.isActiveConversationBlocked;
  activeConversationBlockedType: MessageBlockType =
    InitialData.activeConversationBlockedType;
  activeConversationBlockedCountryId: string =
    InitialData.activeConversationBlockedCountryId;
  activeConversationBlockedCountryLabel: string =
    InitialData.activeConversationBlockedCountryLabel;
  activeConversationAttachments: SharedMedia[] =
    InitialData.activeConversationAttachments;
  activeConversationAttachmentsCount: number =
    InitialData.activeConversationAttachmentsCount;
  activeConversationAttachmentsLoading: boolean =
    InitialData.activeConversationAttachmentsLoading;
  activeMCSharedMediaModalTab: SharedMediaTab =
    InitialData.activeMCSharedMediaModalTab;
  nextAttachmentsUrl: string = InitialData.nextAttachmentsUrl;

  nextMessagesUrl: string = InitialData.nextMessagesUrl;
  futureMessagesUrl: string = InitialData.futureMessagesUrl;
  isMessagesLoading: boolean = InitialData.isMessagesLoading;
  isMessagesLoadingMore: boolean = InitialData.isMessagesLoadingMore;
  isActiveConversationMessagesRefreshing: boolean =
    InitialData.isActiveConversationMessagesRefreshing;

  requestTimeout: any = InitialData.requestTimeout;
  isMessagesMenuInNavbarOpen: boolean = InitialData.isMessagesMenuInNavbarOpen;
  messageFontSize = INITIAL_MESSAGE_FONT_SIZE;
  audits: MessageCenterAuditWarning[] = InitialData.audits;
  notifications: Notification[] = [];
  messageInputFocused: boolean = false;
  canCurrentMemberReceiveMsg: boolean = true;
  canTempMemberReceiveMsg: string[] = [];
  attachedImagesToBeSent: string[] = [];
  imageKeysTobeSubmitted: string[] = [];
  attachmentsTobeSubmitted: MessageAttachmentDetails[] =
    InitialData.attachmentsTobeSubmitted;
  sendMessageError: string | null = InitialData.sendMessageError;
  private bulkMessageId = "_bulk_message";
  private numberOfNowMessagesPreviously = 0;
  private disableMessageListRefresh: boolean = false;
  private isFetchingUnreadConversations = false;
  attachmentsPendingUpload: Array<PendingAttachmentData> = [];
  sendMessageQueue: Array<PendingMessage> = [];
  transcodingCancelTokenSource: CancelTokenSource =
    InitialData.transcodingCancelTokenSource;
  sendMessageCancelTokenSource: CancelTokenSource =
    InitialData.sendMessageCancelTokenSource;

  constructor() {
    makeAutoObservable(this);
  }

  log = (...params: any[]) => {
    logger.log(logPrefix, ...params);
  };

  resetStore = () => {
    Object.entries(InitialData).map(([key, value]) => (this[key] = value));
  };

  get notificationsThatAreNotDismissed() {
    return this.notifications.filter(n => !n.isDismissed);
  }

  dismissNotification = (id: string) => {
    const notification = this.notifications.find(n => n.id === id);
    if (notification?.isDismissable) {
      notification.isDismissed = true;
    }
  };

  get hasAcknowledgedAllAudits() {
    return profileStore.mock2257WarningHasAcknowledgedAllAudits !== null
      ? profileStore.mock2257WarningHasAcknowledgedAllAudits
      : this.audits.every(audit => !audit.unread);
  }

  getRegistrationNotifications = () => {
    const notifications: Notification[] = [];
    if (
      profileStore.isApplicationPending ||
      profileStore.isApplicationIncomplete
    ) {
      notifications.push({
        id: profileStore.isApplicationPending
          ? "application-pending"
          : "application-incomplete",
        type: profileStore.isApplicationPending ? "warning" : "error",
        content: profileStore.isApplicationIncomplete ? (
          <span>
            <FormattedMessage
              id='message-center.notification-application-incomplete'
              defaultMessage='Your Application is incomplete, click read more for more details.'
            />
          </span>
        ) : profileStore.isApplicationPending ? (
          <span>
            <FormattedMessage
              id='message-center.notification-application-pending'
              defaultMessage='Your Application is pending review, click read more for more details.'
            />
          </span>
        ) : (
          <span>
            <FormattedMessage
              id='message-center.notification-application-incomplete'
              defaultMessage='Your Application is incomplete, click read more for more details.'
            />
          </span>
        ),
        onClickReadMore: () =>
          authStore?.openRegisterModalToCompleteProfile(true, true),
        isDismissed: false,
        onClickDismiss: this.dismissNotification,
        isDismissable: false,
      });
    } else if (profileStore.isApplicationDenied) {
      notifications.push({
        id: "application-denied",
        type: "error",
        content: (
          <span>
            <FormattedMessage
              id='message-center.notification-application-denied'
              defaultMessage='Your Application has been denied, click read more for more details.'
            />
          </span>
        ),
        onClickReadMore: () =>
          authStore?.openRegisterModalToCompleteProfile(true, true),
        isDismissed: false,
        onClickDismiss: this.dismissNotification,
        isDismissable: false,
      });
    }

    return notifications;
  };

  setNotifications = (notifications: Notification[]) => {
    this.notifications = notifications;
  };

  initNotifications = async () => {
    const registrationNotifications = this.getRegistrationNotifications();
    const auditWarnings = await this.getAuditWarnings();
    this.setAuditWarnings(auditWarnings);
    const auditNotification =
      this.convertAuditWarningsToNotification(auditWarnings);
    const notifications = registrationNotifications;
    if (auditNotification) {
      notifications.push(auditNotification);
    }
    this.setNotifications(notifications);
  };

  convertAuditWarningsToNotification = (
    auditWarnings: Notification[]
  ): Notification | undefined => {
    if (auditWarnings.length > 0 && !this.hasAcknowledgedAllAudits) {
      const isWarning =
        profileStore.mock2257WarningResult !== undefined
          ? profileStore.mock2257WarningResult === "EMPTY" ||
            profileStore.mock2257WarningResult === "NONE" ||
            profileStore.mock2257WarningResult === ""
          : profileStore.modelProfile.is_broadcast_allowed === undefined
          ? true
          : profileStore.modelProfile.is_broadcast_allowed;
      const audit: Notification = {
        id: MESSAGE_CENTER_AUDIT_NOTIFICATION_ID,
        type: isWarning ? "warning" : "error",
        content: (
          <span>
            {isWarning ? (
              <FormattedMessage
                id='message-center.notification-audit-warning'
                defaultMessage='You have received a warning.'
              />
            ) : (
              <FormattedMessage
                id='message-center.notification-audit-suspension'
                defaultMessage='You have received a suspension.'
              />
            )}
          </span>
        ),
        onClickReadMore: () =>
          modalStore.openPrimaryModal(
            <AccountAuditedDisclaimer isModal={true} />,
            {
              showNativeCloseButton: this.hasAcknowledgedAllAudits,
            }
          ),
        isDismissed: false,
        onClickDismiss: this.dismissNotification,
        isDismissable: false,
      };

      return audit;
    }

    return undefined;
  };

  public sendMessageToMember = async (memberId: string) => {
    this.setSelectedContacts([memberId]);
    await this.createConversation();
  };

  public sendMessageToCS = async () => {
    const agentIdx = Math.floor(Math.random() * this.csContacts.length);
    this.setSelectedContacts([this.csContacts[agentIdx].id]);
    await this.createConversation();
  };

  public get isOnMessagesPage() {
    const url = history.location.pathname;
    return url.indexOf("/messages") !== -1;
  }

  get activeConversationId() {
    if (this.activeConversation) {
      return this.activeConversation.id;
    }
    return null;
  }

  private initSystemMessageUser = async () => {
    const url = "cs-contacts/";
    const { data } = await api.messages.get(url);
    if (data?.length) {
      this.csContacts = (data as MessageCenterContactDetails[]).map(c => {
        c.is_messaging_cs = true;
        c.profile_picture = MC_CS_AGENT_PROFILE_IMG;
        return c;
      });
      const selectedIdx = Math.floor(Math.random() * data?.length);
      const { id, username, email, first_name, last_name } =
        data?.[selectedIdx];
      this.systemMessageUser = { id, username, email, first_name, last_name };
    }
    this.systemMessageUserIds = data?.map(cs_user => {
      return cs_user.id;
    });
  };

  getAuditWarnings = async () => {
    try {
      const url = `my-notifications/?type=audit_warning`;
      const { data } = await api.messages.get(url);
      return data?.results;
    } catch (error) {
      this.log("getAuditWarnings failed");
      return [];
    }
  };

  setAuditWarnings = (audits: MessageCenterAuditWarning[]) => {
    this.audits = audits;
  };

  getPublicNotices = async () => {
    const { userRole } = authStore!;
    const url = `my-notifications/?page_size=${20}&type=public_notice`;
    const { data } = await api.messages.get(url);
    const { data: unreadCountData } = await api.messages.get(
      `my-notifications/unread-count/`
    );
    const unreadCount = unreadCountData?.count;

    if (unreadCount !== undefined) {
      this.unreadNewsAndPolicyCount = unreadCount;
    }

    if (userRole) {
      const news = data?.results
        ?.filter(notice => notice?.action_object?.audience?.includes(userRole))
        .map(notice => {
          return {
            id: notice?.id,
            title: notice?.action_object?.subject,
            body: notice?.action_object?.body,
            start_date: notice?.action_object?.start_date,
            unread_count: notice?.unread ? 1 : 0,
            participants: [
              {
                username: "Streamray",
                first_name: "System",
                last_name: "Administrator",
              },
            ],
          };
        });
      this.newsAndPolicies = news || [];
    }

    this.initSystemMessageUser();
  };

  getAllMessagesCount = async () => {
    try {
      const { data: unreadCountData } = await api.messages.get(
        `conversations/unread-count/`
      );
      this.unreadConversationMessagesCount = unreadCountData?.count;
    } catch (error) {
      this.log("Message Center: Error when getting unread-count", error);
    }
  };

  private waitForUnreadConversations = async () => {
    return await new Promise(resolve => {
      if (!this.isFetchingUnreadConversations) {
        resolve(true);
      } else {
        let attempts = 0;
        const waitForUnreadInterval = setInterval(() => {
          attempts++;
          if (!this.isFetchingUnreadConversations || attempts >= 20) {
            clearInterval(waitForUnreadInterval);
            resolve(true);
          }
        }, 500);
      }
    });
  };

  private updateTempMemberBlock = (
    conversation: MessageCenterConversationDetails
  ) => {
    if (
      conversation.can_receive_message &&
      !this.canCurrentMemberReceiveMsg &&
      conversation?.id === this.activeConversationId
    ) {
      this.setCanCurrentMemberReceiveMsg(true);
    }

    if (
      conversation.can_receive_message &&
      this.canTempMemberReceiveMsg.includes(conversation?.id)
    ) {
      // unblock 24 hr or member reply restriction if a message was received
      const idx = this.canTempMemberReceiveMsg.indexOf(conversation?.id);
      this.canTempMemberReceiveMsg.splice(idx, 1);
      this.canTempMemberReceiveMsg = [...this.canTempMemberReceiveMsg];
    }
  };

  getUnreadConversations = async () => {
    try {
      if (this.conversationsSearchTerm !== "") {
        return;
      }

      this.isFetchingUnreadConversations = true;
      const { data: unreadConversations } = await api.messages.get(
        `conversations/?unread=true`
      );

      const conversations = unreadConversations?.results;
      const mappedConversations: MessageCenterConversationDetails[] =
        conversations?.map(this.getConversationWithoutMemberParticipant) || [];

      await this.getAllMessagesCount();

      let foundNewUnreadMessage = false;
      mappedConversations.forEach(unread_conversation => {
        const target = this.conversations?.find(
          convo => convo?.id === unread_conversation?.id
        );

        this.updateTempMemberBlock(unread_conversation);

        if (
          !target?.last_message ||
          target?.last_message?.id !== unread_conversation?.last_message?.id ||
          target?.unread_count !== unread_conversation?.unread_count
        ) {
          foundNewUnreadMessage = true;
        }
      });

      if (foundNewUnreadMessage) {
        this.conversations = _.unionBy(
          mappedConversations,
          this.conversations,
          "id"
        );
      }

      if (this.activeConversationId) {
        const matchActive = mappedConversations.filter(
          conversationItem => conversationItem.id === this.activeConversationId
        );
        if (matchActive.length) {
          const numberOfUnread = matchActive[0].unread_count;
          await this.getLatestConversationMessages(numberOfUnread);
        }
      }
    } catch (error) {
      this.log("Message Center: Error getting unread conversations", error);
    } finally {
      this.isFetchingUnreadConversations = false;
    }
  };

  get sortedConversations() {
    return [...this.conversations].sort((a, b) => {
      if (a.is_cs_convo && !a.last_message) {
        return -1;
      } else if (b.is_cs_convo && !b.last_message) {
        return 1;
      } else if (a.is_draft) {
        return -1;
      } else if (b.is_draft) {
        return 1;
      } else if (a.last_message && b.last_message) {
        return (
          new Date(b.last_message!.created_at).getTime() -
          new Date(a.last_message!.created_at).getTime()
        );
      } else if (!a.last_message) {
        return 1;
      } else if (!b.last_message) {
        return -1;
      } else {
        return 0;
      }
    });
  }

  getConversations = async (
    loadingMore: boolean = false,
    isRefreshing = false
  ) => {
    if (
      (!loadingMore && !this.isConversationsLoading) ||
      (loadingMore && !this.isConversationsLoadingMore) ||
      !this.isSearchingConversations
    ) {
      try {
        if (
          (loadingMore &&
            this.nextConversationsUrl &&
            this.nextConversationsUrl !== "") ||
          !loadingMore
        ) {
          if (loadingMore) {
            this.isConversationsLoadingMore = true;
          } else if (!isRefreshing) {
            this.isConversationsLoading = true;
          }

          if (this.conversationsSearchTerm !== "") {
            this.isSearchingConversations = true;
          }

          const encodedSearchTerm = encodeURIComponent(
            this.conversationsSearchTerm
          );

          let url;
          if (this.nextConversationsUrl !== "" && loadingMore) {
            url = `conversations/${this.nextConversationsUrl.split("/").pop()}`;
          } else {
            url = `conversations/?page_size=${20}&search=${encodedSearchTerm}`;
          }

          const { data } = await api.messages.get(url);

          const conversations = data?.results;
          const mappedConversations =
            conversations?.map(this.getConversationWithoutMemberParticipant) ||
            [];

          if (!this.csContacts || this.csContacts.length === 0) {
            await this.initSystemMessageUser();
          }

          const draftConversations = this.conversations.filter(
            conversation => conversation.is_draft
          );
          const csConvos = mappedConversations.filter(
            conversation => conversation.is_cs_convo
          );
          const notCsConvos = mappedConversations.filter(
            conversation => !conversation.is_cs_convo
          );
          const existingCSConvo = !loadingMore
            ? mappedConversations.find(conversation => conversation.is_cs_convo)
            : this.conversations.find(conversation => conversation.is_cs_convo);

          if (!existingCSConvo) {
            if (csConvos.length > 0) {
              this.csConversation = csConvos[0];
            } else {
              this.csConversation = {
                id: "cs-convo",
                participants: this.csContacts,
                can_receive_message: true,
                unread_count: 0,
                is_cs_convo: true,
              };
            }
          }

          if (!loadingMore) {
            this.conversations = [
              ...(!existingCSConvo ? [this.csConversation] : []),
              ...draftConversations,
              ...mappedConversations,
            ];
          } else {
            this.conversations = [...this.conversations, ...notCsConvos];
          }

          const nextUrl = data?.next;
          if (nextUrl && nextUrl !== "") {
            this.nextConversationsUrl = nextUrl;
          } else {
            this.nextConversationsUrl = "";
          }
        }
      } catch (error) {
        this.log("Message Center: Error getting conversations", error);
        /*snackbarStore.enqueueSnackbar({
          message: {
            id: 'messages.error.get-conversations',
            default: 'There was an error while getting conversations'
          },
          variant: SnackbarVariants.ERROR
        })*/
      } finally {
        if (loadingMore) {
          this.isConversationsLoadingMore = false;
        } else {
          this.isConversationsLoading = false;
        }

        if (this.conversationsSearchTerm !== "") {
          this.isSearchingConversations = false;
        }
      }
    }
  };

  createConversation = async () => {
    try {
      // if there is already a conversation that is draft, clear it out so
      this.conversations = this.conversations.filter(
        conversation => !conversation.is_draft
      );
      let conversation: MessageCenterConversationDetails;

      if (this.isBulkMessageView) {
        const participants: MessageCenterContactDetails[] = [];
        this.selectedContacts.forEach(id => {
          const selected_contact = this.contacts.find(
            _contact => _contact?.id === id
          );
          selected_contact && participants.push(selected_contact);
        });

        conversation = {
          id: this.bulkMessageId,
          participants,
          unread_count: 0,
          is_draft: true,
          can_receive_message: true,
        };

        this.conversations = [...this.conversations, conversation];
        this.resetActiveConversation();
      } else {
        const { data } = await api.messages.post(
          {
            participants: this.selectedContacts,
          },
          `conversations/`
        );
        conversation = this.getConversationWithoutMemberParticipant(data);

        if (conversation) {
          // if there is no last message
          // it means its a fresh conversation
          if (!conversation?.last_message) {
            conversation.is_draft = true;
          }

          const conversationExists = this.conversations.find(
            conversation_ => conversation_.id === conversation.id
          );

          if (!conversationExists) {
            this.conversations = [conversation, ...this.conversations];
          }

          this.setSelectedContacts([]);
          this.isComposing = false;
        }
      }

      this.isSearchingView = false;
      this.isSearchingConversations = false;
      this.setContactsSearchTerm("");
      this.setConversationsSearchTerm("");
      await this.readConversation(conversation?.id);
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "messages.error.start-conversation",
          default: "There was an error while starting conversation",
        },
        variant: SnackbarVariants.ERROR,
      });
      this.log("Message Center: Error creating conversation", error);
    }
  };

  getLatestConversationMessages = async (unreadCount: number) => {
    const conversationId = this.activeConversationId;
    if (conversationId && !this.isMessagesLoading) {
      try {
        const url = `conversations/${conversationId}/messages/?page_size=${unreadCount}&ordering=-created_at`;
        const { data } = await api.messages.get(url);

        // messages need to be reversed to have whatsapp like behavior
        const sortedConversationMessages =
          this.sortConversationMessagesByNewest(data?.results).reverse();

        // add messages to list
        this.activeConversationMessages = [
          ...sortedConversationMessages,
          ...this.activeConversationMessages,
        ];

        // for protection you can only mark the messages you receive as read, not your own messages
        const receivedMessageIdsToMarkAsRead = sortedConversationMessages
          .filter(
            message =>
              message.sender.username !==
                profileStore.modelProfile.screen_name && message.unread
          )
          .map(message => message.id);

        if (receivedMessageIdsToMarkAsRead?.length > 0) {
          this.unreadConversationMessagesCount -=
            receivedMessageIdsToMarkAsRead?.length;
          await this.markMessagesAsRead(
            conversationId,
            receivedMessageIdsToMarkAsRead
          );
        }

        const mappedConversationMessages = this.activeConversationMessages.map(
          conversation => {
            if (receivedMessageIdsToMarkAsRead.includes(conversation.id)) {
              conversation.unread = false;
            }
            return conversation;
          }
        );

        // mark new messages as read
        this.activeConversationMessages = mappedConversationMessages;
      } catch (error) {
        this.log("Message Center: Error getting messages", error);
        snackbarStore.enqueueSnackbar({
          message: {
            id: "messages.error.get-messages",
            default: "There was an error while getting messages",
          },
          variant: SnackbarVariants.ERROR,
        });
      }
    }
  };

  csResolveConvo = async (id: string) => {
    await api.messages.post(`conversations/${id}/resolve/`);
    await this.getConversations(false, true);
    this.activeConversation =
      this.conversations.find(
        convo => convo?.id === this.activeConversationId
      ) || this.activeConversation;
  };

  csUnResolveConvo = async (id: string) => {
    await api.messages.post(`conversations/${id}/unresolve/`);
    await this.getConversations(false, true);
    this.activeConversation =
      this.conversations.find(
        convo => convo?.id === this.activeConversationId
      ) || this.activeConversation;
  };

  refreshActiveConversationMessages = async () => {
    if (this.activeConversationId && !this.disableMessageListRefresh) {
      this.getActiveConversationMessages(
        false,
        this.activeConversationId,
        true
      );
    }
  };

  private handleActiveConversationMessages = async (
    data,
    conversationId: string,
    {
      loadingMore = false,
      isRefreshing = false,
      skipSort = false,
      loadingMoreType = undefined as MessagesLoadMoreType,
    }
  ) => {
    const activeConversationMember =
      this.activeConversation?.participants?.find(
        participant => participant?.profile_type === "member"
      );
    let messagesParticipant = ""; // the member who these messages are from

    // messages need to be reversed to have whatsapp like behavior
    const sortedConversationMessages = skipSort
      ? data?.results
      : this.sortConversationMessagesByNewest(data?.results).reverse();

    // for protection you can only mark the messages you receive as read, not your own messages
    const receivedMessageIdsToMarkAsRead = sortedConversationMessages
      .filter(
        message =>
          message.sender.username !== profileStore.modelProfile.screen_name &&
          message.unread
      )
      .map(message => message.id);

    if (receivedMessageIdsToMarkAsRead?.length > 0) {
      await this.markMessagesAsRead(
        conversationId,
        receivedMessageIdsToMarkAsRead
      );
    }

    const mappedConversationMessages = sortedConversationMessages.map(
      conversation => {
        if (
          !messagesParticipant &&
          conversation?.sender?.profile_type === "member"
        ) {
          messagesParticipant = conversation?.sender?.username;
        }

        if (receivedMessageIdsToMarkAsRead.includes(conversation.id)) {
          conversation.scroll_to = conversation.unread;
          conversation.unread = false;
        }
        return conversation;
      }
    );

    if (
      !this.activeConversation?.is_cs_convo &&
      messagesParticipant &&
      messagesParticipant !== activeConversationMember?.username
    ) {
      return;
    }

    if (isRefreshing) {
      const numberOfDeniedMessagesPreviously =
        this.activeConversationMessages.filter(
          message => message.status === "DENIED"
        ).length;
      const numberOfDeniedMessagesCurrently = mappedConversationMessages.filter(
        message => message.status === "DENIED"
      ).length;

      const numberOfNowMessages = mappedConversationMessages.filter(
        message =>
          dateProcess?.getHumanReadableDate(message?.created_at) === "Now"
      ).length;

      const currentMostRecentMessage = this.activeConversationMessages?.[0];
      const incomingMostRecentMessage =
        mappedConversationMessages?.[0] as MessageCenterMessageDetails;

      const hasReply = this.activeConversationMessages.find(
        msg => msg.has_reply === false
      );
      // when polling message list, we want to limit the amount of times we repaint
      if (
        numberOfDeniedMessagesCurrently > numberOfDeniedMessagesPreviously ||
        numberOfNowMessages < this.numberOfNowMessagesPreviously ||
        currentMostRecentMessage?.id !== incomingMostRecentMessage?.id ||
        currentMostRecentMessage?.status === "PROCESSING" ||
        hasReply
      ) {
        this.activeConversationMessages = mappedConversationMessages;

        if (
          this.activeConversation &&
          incomingMostRecentMessage &&
          this.conversations
        ) {
          // update active conversation with last message received
          await this.getConversations(false, true);
          this.activeConversation =
            this.conversations.find(
              convo => convo?.id === this.activeConversationId
            ) || this.activeConversation;
          this.updateTempMemberBlock(this.activeConversation);

          this.activeConversation = {
            ...this.activeConversation,
            last_message: incomingMostRecentMessage,
          };
        }
      }

      this.numberOfNowMessagesPreviously = numberOfNowMessages;
    } else if (!loadingMore) {
      this.activeConversationMessages = mappedConversationMessages;
    } else if (loadingMore && loadingMoreType === "next") {
      this.activeConversationMessages = [
        ...mappedConversationMessages,
        ...this.activeConversationMessages,
      ];
    } else {
      this.activeConversationMessages = [
        ...this.activeConversationMessages,
        ...mappedConversationMessages,
      ];
    }

    const nextUrl = data?.next;
    if (
      loadingMore &&
      loadingMoreType === "next" &&
      nextUrl &&
      nextUrl !== ""
    ) {
      this.futureMessagesUrl = nextUrl;
    } else if (loadingMore && loadingMoreType === "next") {
      this.futureMessagesUrl = "";
    } else if (nextUrl && nextUrl !== "") {
      this.nextMessagesUrl = nextUrl;
    } else {
      this.nextMessagesUrl = "";
    }
  };

  handleFutureMessagesData = async (data, conversationId) => {
    // created_at filter has issues omitting some messages when using -created_at ordering so we need to reverse them
    const sorted_results = {
      ...data,
      results: data?.results?.reverse() || [],
    };
    await this.handleActiveConversationMessages(
      sorted_results,
      conversationId,
      {
        loadingMore: true,
        isRefreshing: false,
        skipSort: true,
        loadingMoreType: "next",
      }
    );
  };

  getActiveConversationMessagesFromStartDate = async (
    startAtDate: string,
    loadingMore: boolean = false,
    loadingMoreType?: MessagesLoadMoreType
  ) => {
    const conversationId =
      this.activeConversation?.id || this.activeConversationId || "";
    const defaultLimit = MESSAGES_PER_REQUEST;
    this.disableMessageListRefresh = true;
    this.searchingMessagesStartDate = startAtDate;

    if (!loadingMore) {
      this.activeConversationMessages = [];
    }

    if (
      (conversationId && !loadingMore && !this.isMessagesLoading) ||
      (loadingMore && !this.isMessagesLoadingMore)
    ) {
      try {
        if (
          (loadingMore &&
            this.nextMessagesUrl &&
            this.nextMessagesUrl !== "") ||
          (loadingMore &&
            this.futureMessagesUrl &&
            this.futureMessagesUrl !== "") ||
          !loadingMore
        ) {
          if (loadingMore) {
            this.isMessagesLoadingMore = true;
          } else {
            // reset next and previous urls when new search result is opened
            this.nextMessagesUrl = "";
            this.futureMessagesUrl = "";
          }

          const url =
            loadingMoreType === "next"
              ? `conversations/${conversationId}/messages/?page_size=${defaultLimit}&created_at__gt=${startAtDate}&ordering=created_at`
              : loadingMoreType === "previous"
              ? `conversations/${conversationId}/messages/?page_size=${defaultLimit}&created_at__lt=${startAtDate}&ordering=-created_at`
              : `conversations/${conversationId}/messages/?page_size=${defaultLimit}&created_at__lte=${startAtDate}&ordering=-created_at`;
          const { data } = await api.messages.get(url);

          if (loadingMoreType === "next") {
            await this.handleFutureMessagesData(data, conversationId);
          } else {
            await this.handleActiveConversationMessages(data, conversationId, {
              loadingMore,
              isRefreshing: false,
              skipSort: true,
              loadingMoreType,
            });
          }

          if (!loadingMore && !loadingMoreType) {
            // fetch a second page for search results so we can scroll down
            const future_messages_url = `conversations/${conversationId}/messages/?page_size=${defaultLimit}&created_at__gt=${startAtDate}&ordering=created_at`;
            const { data: data_future } = await api.messages.get(
              future_messages_url
            );
            await this.handleFutureMessagesData(data_future, conversationId);
          }
        }
      } catch (error) {
        this.log(
          "Message Center: Error getting messages from search result position",
          error
        );
        snackbarStore.enqueueSnackbar({
          message: {
            id: "messages.error.get-messages",
            default: "There was an error while getting messages",
          },
          variant: SnackbarVariants.ERROR,
        });
      } finally {
        if (loadingMore) {
          this.isMessagesLoadingMore = false;
        } else {
          this.isMessagesLoading = false;
        }
      }
    }
  };

  getSearchedConversationPreviousMessages = async () => {
    if (this.activeConversationMessages.length) {
      this.getActiveConversationMessagesFromStartDate(
        this.activeConversationMessages[
          this.activeConversationMessages.length - 1
        ].created_at,
        true,
        "previous"
      );
    }
  };

  getSearchedConversationFutureMessages = async () => {
    if (this.activeConversationMessages.length) {
      this.getActiveConversationMessagesFromStartDate(
        this.activeConversationMessages[0].created_at,
        true,
        "next"
      );
    }
  };

  getActiveConversationMessagesByLimit = async (
    conversation_id: string,
    limit: number
  ): Promise<{
    next: string;
    previous: string;
    results: MessageCenterMessageDetails[];
  }> => {
    const url = `conversations/${conversation_id}/messages/?page_size=${limit}&ordering=-created_at`;
    const { data } = await api.messages.get(url);

    return data;
  };

  getActiveConversationMessages = async (
    loadingMore: boolean = false,
    activeConversationId?: string,
    isRefreshing: boolean = false
  ) => {
    const conversationId =
      activeConversationId || this.activeConversationId || "";
    const defaultLimit = MESSAGES_PER_REQUEST;
    const refreshingLimit =
      this.activeConversationMessages.length > defaultLimit
        ? this.activeConversationMessages.length
        : defaultLimit;
    this.disableMessageListRefresh = false;
    this.isActiveConversationMessagesRefreshing = isRefreshing;

    if (!loadingMore && !isRefreshing) {
      this.sendMessageError = null;
    }

    if (loadingMore && this.searchingMessagesStartDate) {
      await this.getSearchedConversationPreviousMessages();
    } else if (
      (conversationId && !loadingMore && !this.isMessagesLoading) ||
      (loadingMore && !this.isMessagesLoadingMore)
    ) {
      try {
        this.searchingMessagesStartDate = "";
        if (
          (loadingMore &&
            this.nextMessagesUrl &&
            this.nextMessagesUrl !== "") ||
          !loadingMore
        ) {
          if (loadingMore) {
            this.isMessagesLoadingMore = true;
          } else if (!isRefreshing) {
            this.isMessagesLoading = true;
          }

          let message_data;
          if (this.nextMessagesUrl !== "" && loadingMore) {
            const url = this.nextMessagesUrl.split("notifications/")[1];
            const { data } = await api.messages.get(url);
            message_data = data;
          } else if (isRefreshing) {
            message_data = await this.getActiveConversationMessagesByLimit(
              conversationId,
              refreshingLimit
            );
          } else {
            message_data = await this.getActiveConversationMessagesByLimit(
              conversationId,
              defaultLimit
            );
          }

          await this.handleActiveConversationMessages(
            message_data,
            conversationId,
            {
              loadingMore,
              isRefreshing,
            }
          );
        }
      } catch (error) {
        this.log("Message Center: Error getting messages", error);
        snackbarStore.enqueueSnackbar({
          message: {
            id: "messages.error.get-messages",
            default: "There was an error while getting messages",
          },
          variant: SnackbarVariants.ERROR,
        });
      } finally {
        if (loadingMore) {
          this.isMessagesLoadingMore = false;
        } else {
          this.isMessagesLoading = false;
        }

        if (this.isActiveConversationMessagesRefreshing) {
          this.isActiveConversationMessagesRefreshing = false;
        }
      }
    }
  };

  get conversationsGroupedByParticipants() {
    const groupedConversations = _.groupBy(
      this.conversations,
      (conversation: MessageCenterConversationDetails) => {
        return conversation.participants.map(
          participant => participant.username
        );
      }
    );
    return groupedConversations;
  }

  get hasMoreMessages(): boolean {
    return this.nextMessagesUrl !== "";
  }

  get hasMoreFutureMessages(): boolean {
    return this.futureMessagesUrl !== "";
  }

  get hasMoreConversations(): boolean {
    return this.nextConversationsUrl !== "";
  }

  get hasMoreContacts(): boolean {
    return this.nextContactsUrl !== "";
  }

  public markMessagesAsRead = async (
    conversationId,
    messageIdOrIds: string[] | number[]
  ) => {
    try {
      if (this.isFetchingUnreadConversations) {
        // wait for unread conversations to complete to avoid race condition with unread count
        await this.waitForUnreadConversations();
      }

      const countOfMessages = messageIdOrIds.length;
      this.conversations = this.conversations.map(conversation => {
        if (conversation.id === conversationId) {
          conversation.unread_count -= countOfMessages;
        }

        return conversation;
      });

      if (this.activeConversation) {
        this.activeConversation = {
          ...this.activeConversation,
          unread_count: this.activeConversation.unread_count - countOfMessages,
        };
      }

      this.activeConversationMessages = this.activeConversationMessages.map(
        message => {
          if (messageIdOrIds.includes(message.id as never)) {
            message.unread = false;
          }
          return message;
        }
      );

      this.unreadConversationMessagesCount -= countOfMessages;
    } catch (error) {
      this.log("Message Center: Error marking messages as read", error);
    }
  };

  public sortConversationMessagesByNewest = (
    messages: MessageCenterMessageDetails[]
  ) => {
    if (messages) {
      return messages.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    }

    return messages;
  };

  private getConversationWithoutMemberParticipant = (
    conversation: MessageCenterConversationDetails
  ) => {
    if (conversation) {
      const userName = profileStore.modelProfile?.screen_name || "";

      const convo = {
        ...conversation,
        participants: conversation?.participants?.filter(
          participant => participant.username !== userName
        ),
      };
      if (convo.participants && convo.participants.length > 0) {
        convo.participants = convo.participants.map(p => {
          if (p.is_messaging_cs) {
            convo.is_cs_convo = true;
            if (!this.systemMessageUserIds.includes(p.id)) {
              this.systemMessageUserIds.push(p.id);
            }
            p.profile_picture = MC_CS_AGENT_PROFILE_IMG;
          }
          return p;
        });
      }
      return convo;
    }

    return conversation;
  };

  public getContacts = async (
    loadingMore: boolean = false,
    type?: string[]
  ) => {
    this.contactsFilterTypes = type || [];

    if (type?.length) {
      let filtered_contacts: MessageCenterContactDetails[] = [];
      type.forEach(t => {
        const contact_list = this.contactsSearchTerm
          ? this.searchedContacts
          : this.unFilteredContacts;
        const type_filtered = contact_list.filter(contact => {
          const filters = LoyalFanFiltersKey[t];
          return filters.some(filter => contact[filter]);
        });
        filtered_contacts = _.unionBy(type_filtered, filtered_contacts, "id");
        filtered_contacts = filtered_contacts.sort((a, b) =>
          a.username.localeCompare(b.username)
        );
      });
      this.contacts = filtered_contacts;

      return filtered_contacts;
    } else if (
      !loadingMore &&
      this.unFilteredContacts.length &&
      !this.contactsSearchTerm
    ) {
      this.contacts = this.unFilteredContacts;
      return this.unFilteredContacts;
    }

    if (
      (!loadingMore && !this.isContactsLoading) ||
      (loadingMore && !this.isContactsLoadingMore) ||
      !this.isSearchingContacts
    ) {
      try {
        if (
          (loadingMore &&
            this.nextContactsUrl &&
            this.nextContactsUrl !== "") ||
          !loadingMore
        ) {
          if (loadingMore) {
            this.isContactsLoadingMore = true;
          } else {
            this.isContactsLoading = true;
          }

          if (this.contactsSearchTerm !== "") {
            this.isSearchingContacts = true;
          }

          const encodedSearchTerm = encodeURIComponent(this.contactsSearchTerm);
          const selectedType = type ? type.map(type => `&type=${type}`) : "";
          let url;

          if (this.nextContactsUrl !== "" && loadingMore) {
            url = `contacts/${this.nextContactsUrl.split("/").pop()}${[
              ...selectedType,
            ].join("")}`;
          } else {
            url = `contacts/?limit=${100}&search=${encodedSearchTerm}${[
              ...selectedType,
            ].join("")}`;
          }
          const { data } = await api.messages.get(url);

          const contacts = data?.results;

          if (this.isSearchingContacts) {
            this.searchedContacts = loadingMore
              ? [...this.searchedContacts, ...contacts]
              : contacts;
          } else {
            // unfiltered contact list
            this.unFilteredContacts = loadingMore
              ? [...this.unFilteredContacts, ...contacts]
              : contacts;
          }

          const nextUrl = data?.next;
          if (nextUrl && nextUrl !== "") {
            this.nextContactsUrl = nextUrl;
            //   this.contacts = [...this.contacts, contacts];
          } else {
            this.nextContactsUrl = "";
            this.contacts = this.isSearchingContacts
              ? this.searchedContacts
              : this.unFilteredContacts;
          }
        }
      } catch (error) {
        snackbarStore.enqueueSnackbar({
          message: {
            id: "messages.error.get-contacts",
            default: "There was an error while getting contacts",
          },
          variant: SnackbarVariants.ERROR,
        });
        this.log("Message Center: Error getting contacts", error);
      } finally {
        if (loadingMore) {
          this.isContactsLoadingMore = false;
        } else {
          this.isContactsLoading = false;
        }

        if (this.contactsSearchTerm !== "") {
          this.isSearchingContacts = false;
        }

        // automatically load next page until we fetch all contacts
        if (this.nextContactsUrl) {
          this.getContacts(true);
        }
      }
    }
  };

  public getLoyalFansContacts = async () => {
    const types = Object.keys(LoyalFanFiltersEnum).filter(
      key => LoyalFanFiltersEnum[key]
    );
    this.getContacts(false, types);
  };

  get unreadConversations() {
    let unreadConversations: MessageCenterConversationDetails[] = [];
    if (this.conversations && this.conversations.length) {
      unreadConversations = this.conversations.filter(
        conversation => conversation.unread_count > 0
      );
    }

    return unreadConversations;
  }

  readConversation = async (id: string | null, startAtDate?: string) => {
    if (this.activeConversation?.id !== id || startAtDate) {
      this.unBlockActiveConversation();
      const conversation = this.conversations.find(
        conversation => conversation.id === id
      );
      this.activeConversation = conversation || null;
      this.activeSearchedWord = "";
      this.activeNewsAndPolicy = null;
      if (conversation && !this.isBulkMessageView && startAtDate) {
        this.activeSearchedWord = this.conversationsSearchTerm;
        this.getActiveConversationMessagesFromStartDate(startAtDate);
      } else if (conversation && !this.isBulkMessageView) {
        await this.getActiveConversationMessages();
        if (this.isSearchingView) {
          this.setIsSearchingView(false);
          this.setConversationsSearchTerm("");
        }
      } else {
        this.activeConversationMessages = [];
      }
      this.setMessage("");
      this.setSelectedConversations([]);

      if (this.attachedImagesToBeSent.length > 0) {
        this.setAttachImagesToBeSent([]);
      }

      // get blocked status
      if (
        this.activeConversation &&
        this.activeConversation?.participants?.length > 0
      ) {
        const activeConvoWithoutUser =
          this.getConversationWithoutMemberParticipant(this.activeConversation);
        this.getBlockedConversationStatus(
          activeConvoWithoutUser?.participants?.[0]?.id
        );
      }
    }
  };

  readNewsAndPolicy = (id: string | null) => {
    const notice = this.newsAndPolicies.find(notice => notice.id === id);
    this.activeConversation = this.activeNewsAndPolicy = notice || null;
    this.setMessage("");
    this.setSelectedConversations([]);
    if (notice?.unread_count) {
      this.markNotificationsAsRead([notice.id]);
    }
  };

  getBlockedConversationStatus = async memberId => {
    const url = `conversations/is-receiver-blocked/?receiver_id=${memberId}`;
    const { data } = await await api.messages.get(url);
    const blocked_data = data as MessageCenterConversationBlockedData;

    if (
      blocked_data?.is_member_blocked_by_model ||
      blocked_data?.is_member_country_blocked_by_model ||
      blocked_data?.is_member_state_blocked_by_model
    ) {
      this.isActiveConversationBlocked = true;

      if (blocked_data?.is_member_blocked_by_model) {
        this.activeConversationBlockedType = "member";
      } else if (blocked_data?.is_member_country_blocked_by_model) {
        const country_data =
          blocked_data?.blocked_state_or_country_data?.country;
        this.activeConversationBlockedType = "country";
        this.activeConversationBlockedCountryId = country_data?.country_id;
        this.activeConversationBlockedCountryLabel =
          country_data?.country_label;
      } else if (blocked_data?.is_member_state_blocked_by_model) {
        const state_data = blocked_data?.blocked_state_or_country_data?.state;
        this.activeConversationBlockedType = "state";
        this.activeConversationBlockedCountryId = state_data?.country_id;
        this.activeConversationBlockedCountryLabel = state_data?.state_label;
      }
    }
  };

  unBlockActiveConversation = () => {
    this.isActiveConversationBlocked = false;
    this.activeConversationBlockedType = null;
    this.activeConversationBlockedCountryId = "";
    this.activeConversationBlockedCountryLabel = "";
  };

  resetActiveConversation = () => {
    this.activeConversation = null;
    this.activeNewsAndPolicy = null;
    this.activeConversationMessages = [];
    this.unBlockActiveConversation();
    this.clearConversationAttachments();
  };

  markNotificationsAsRead = async (
    notifications: string[],
    inMemoryUpdate: boolean = false,
    refreshPublicNotices: boolean = true
  ) => {
    try {
      if (!inMemoryUpdate) {
        await api.messages.post(
          { notifications },
          `my-notifications/mark-read/`
        );
      }
      if (refreshPublicNotices) {
        await this.getPublicNotices();
      }

      this.setNotifications(
        this.notifications.map(n => ({
          ...n,
          isDismissed: notifications.includes(n.id) ? true : n.isDismissed,
        }))
      );
    } catch (error) {
      this.log("Message Center: Error marking notifications as read", error);
    }
  };

  setMessage = (message: string) => {
    this.message = message.slice(0, 500);
    this.setMessageCount(message);
  };

  setMessageCount = (message: string) => {
    if (message.length > 500) {
      this.messageCharCount = 500;
    } else {
      this.messageCharCount = message.length;
    }
  };

  sendMessage = async (
    noticeBlock?: string,
    messageId?: string,
    onProgress?: (event: ProgressEvent) => void
  ) => {
    if (this.isSendingMessage) {
      return;
    }

    if (
      noticeBlock ||
      this.message.trim() !== "" ||
      this.attachedImagesToBeSent?.length
    ) {
      this.isSendingMessage = true;
      try {
        const limit = Number(config.mcCharLimit);
        const queue: MessageQueueChunk[] = [];
        const toBeSent = noticeBlock || this.message || "";
        if (!toBeSent || toBeSent.length <= limit) {
          const cleanedMessage = sanitizeHtml(toBeSent);
          const parsedMessage =
            new DOMParser().parseFromString(cleanedMessage, "text/html").body
              .textContent || "";
          const message = parsedMessage.slice(0, 500);
          if (this.isMCPhase2Active) {
            queue.push({
              data: {
                message,
              },
              attachments: this.attachmentsTobeSubmitted,
            });
          } else {
            queue.push({
              data: {
                message,
              },
              image_keys: this.imageKeysTobeSubmitted,
            });
          }
        } else {
          const words = toBeSent.split(" ");
          let sink = "";
          words.forEach((_word: string) => {
            const word = _word.replace(/ /g, "");
            if (sink.length + word.length + 1 < limit) {
              sink += `${word} `;
            } else if (word.length > limit) {
              for (let i = 0; i < word.length; i++) {
                if (sink.length < limit) {
                  sink += word[i];
                } else {
                  queue.push({
                    data: {
                      message: sink,
                    },
                    image_keys: [],
                  });
                  sink = "";
                  sink += word[i];
                }
                if (i === word.length - 1) {
                  sink += " ";
                }
              }
            } else {
              queue.push({
                data: {
                  message: sink,
                },
                image_keys: [],
              });
              sink = "";
              sink += `${word} `;
            }
          });
          if (sink.length > 0) {
            queue.push({
              data: {
                message: sink,
              },
              image_keys: [],
            });
          }
          if (
            this.attachedImagesToBeSent &&
            this.attachedImagesToBeSent.length > 0 &&
            queue.length > 0
          ) {
            queue[queue.length - 1].image_keys = this.attachedImagesToBeSent;
          }
        }

        for (const x of queue) {
          if (
            x?.image_keys?.length ||
            x?.attachments?.length ||
            x.data?.message
          )
            this.queueSendMessage(
              this.activeConversationId,
              x,
              messageId,
              onProgress
            );
        }

        this.setMessage("");
        this.sendMessageError = null;
      } catch (error) {
        snackbarStore.enqueueSnackbar({
          message: {
            id: "messages.error.send-message",
            default: "There was an error while sending message",
          },
          variant: SnackbarVariants.ERROR,
        });

        this.log("Message Center: Error sending message", error);
        this.sendMessageError =
          error?.response?.data?.non_field_errors?.[1]?.message_i18n ||
          error?.response?.data?.non_field_errors?.[0] ||
          null;
      }
    }
  };

  appendNewMessageToActiveConversation = async (
    message: MessageCenterMessageDetails
  ) => {
    const newMessages = [
      { ...message, status: "PROCESSING" } as MessageCenterMessageDetails,
      ...this.activeConversationMessages,
    ];
    this.activeConversationMessages =
      this.sortConversationMessagesByNewest(newMessages).reverse();
  };

  private queueSendMessage = async (
    conversationId,
    chunk: MessageQueueChunk,
    messageId?: string,
    onProgress?: (event: ProgressEvent) => void
  ) => {
    let send_endpoint = `conversations/${conversationId}/messages/`;
    let req_data: MessageRequestData = chunk;
    const isBulkMessage = conversationId === this.bulkMessageId;

    if (isBulkMessage) {
      send_endpoint = "conversations/send-bulk-message-to-loyal-members/";
      req_data = {
        message_data: {
          message: chunk?.data?.message,
        },
        image_keys: chunk?.image_keys,
        attachments: chunk?.attachments || [],
        participants: this.selectedContacts,
      };
    } else if (this.isMCPhase2Active && chunk?.attachments?.length) {
      send_endpoint = `conversations/${conversationId}/messages/`;
      const form_data = {
        data: JSON.stringify(chunk.data),
      };
      chunk.attachments?.forEach((attachment, index) => {
        const idx = index > 0 ? index + 1 : "";
        form_data[`attachment_nfs${idx}`] = attachment.file;
        form_data[`attachment_price${idx}`] = attachment.price;
        if (attachment.length) {
          form_data[`attachment_length${idx}`] = parseFloat(
            attachment.length.toFixed(2)
          );
        }
      });

      req_data = createCustomFormData(form_data);
    }

    const hasPendingAttachments = !!this.attachmentsPendingUpload.filter(
      attachment =>
        attachment.message_id === messageId && attachment.progress !== 100
    ).length;

    if (hasPendingAttachments && messageId) {
      // since there are attachments currently uploading, we need message id to reference this send request
      this.sendMessageQueue = [
        ...this.sendMessageQueue,
        {
          message_id: messageId,
          conversation_id: conversationId,
          is_bulk_message: isBulkMessage,
          request_data: req_data,
          send_endpoint,
        },
      ];
    } else {
      this.processQueuedSendMessage(
        conversationId,
        req_data,
        send_endpoint,
        isBulkMessage,
        onProgress,
        MESSAGE_LIST_REFRESH_DELAY_IN_MS
      );
    }
  };

  private processQueuedSendMessage = async (
    conversationId,
    requestData,
    sendEndpoint,
    isBulkMessage?: boolean,
    onProgress?: (event: ProgressEvent) => void,
    refreshDelay = 0
  ) => {
    try {
      this.sendMessageCancelTokenSource = cancelToken.source();

      const isActiveConversation = this.activeConversationId === conversationId;
      const { data } = await api.messages.post(
        requestData,
        sendEndpoint,
        {},
        {
          cancelToken: this.sendMessageCancelTokenSource.token,
          onUploadProgress: (event: ProgressEvent) => {
            if (isActiveConversation) {
              onProgress?.(event);
            }
          },
        }
      );

      if (isBulkMessage && isActiveConversation) {
        this.setIsBulkMessageView(false);
        this.setIsComposing(false);
        this.setIsSearchingView(false);
        this.unSelectAllContacts();
        this.readConversation(null);
      } else {
        const hasProcessingVideo = !!data?.attachments_data?.find(
          attachment =>
            !attachment.thumbnail_url &&
            attachment.attachment_type == SharedMediaTypeEnum.Video
        );

        if (isActiveConversation && hasProcessingVideo) {
          setTimeout(() => {
            this.waitForThumbnail(data?.id, conversationId);
          }, refreshDelay);
        } else if (isActiveConversation) {
          this.appendNewMessageToActiveConversation(data);
          setTimeout(() => {
            this.refreshActiveConversationMessages();
          }, refreshDelay);
        }

        this.conversations = this.conversations.map(conversation => {
          if (conversation.id === conversationId) {
            conversation.last_message = data;
            if (conversation.is_draft) {
              conversation.is_draft = false;
            }
          }
          return conversation;
        });
      }

      if (
        !data.can_receive_message &&
        this.activeConversationId &&
        isActiveConversation
      )
        this.setTempCanMemberReceiveMsg(this.activeConversationId);
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "messages.error.send-message",
          default: "There was an error while sending message",
        },
        variant: SnackbarVariants.ERROR,
      });

      this.log("Message Center: Error sending message", error);
      this.sendMessageError =
        error?.response?.data?.non_field_errors?.[1]?.message_i18n ||
        error?.response?.data?.non_field_errors?.[0] ||
        null;
    } finally {
      this.setAttachImagesToBeSent([]);
      this.isSendingMessage = false;
    }
  };

  private waitForThumbnail = async (
    message_id: string,
    conversation_id: string
  ) => {
    try {
      await exponentialBackoffWithJitter(
        async () => {
          if (this.activeConversationId === conversation_id) {
            const { results } = await this.getActiveConversationMessagesByLimit(
              conversation_id,
              1
            );
            const hasProcessingVideo = !!results?.[0]?.attachments_data?.find(
              attachment =>
                !attachment.thumbnail_url &&
                attachment.attachment_type === SharedMediaTypeEnum.Video
            );

            if (message_id === results?.[0]?.id && hasProcessingVideo) {
              throw new Error("Video thumbnail is still missing");
            } else if (message_id === results?.[0]?.id) {
              this.appendNewMessageToActiveConversation(results?.[0]);
            }
          }
        },
        MESSAGE_THUMBNAIL_REFRESH_MAX_ATTEMPTS,
        MESSAGE_LIST_REFRESH_DELAY_IN_MS
      );
    } catch (error) {
      this.log(
        "Message Center: Maximum attempts exceeded while waiting for thumbnail"
      );
    }
  };

  cancelSendMessage = (message_id: string) => {
    this.cancelPendingAttachments(message_id);

    this.attachmentsPendingUpload = this.attachmentsPendingUpload.filter(
      attachment => attachment.message_id !== message_id
    );
    this.sendMessageQueue = this.sendMessageQueue.filter(
      item => item.message_id !== message_id
    );
  };

  @action
  setContactsSearchTerm = (term: string) => {
    this.contactsSearchTerm = term;

    if (term === "") {
      this.nextContactsUrl = "";
    }
  };

  @action
  setConversationsSearchTerm = (term: string) => {
    this.conversationsSearchTerm = term;
    this.getConversations();

    if (term === "") {
      this.nextConversationsUrl = "";
    }
  };

  deleteNewsAndPolicy = async (notice: MessageCenterConversationDetails) => {
    try {
      if (this.systemMessageUser) {
        await this.sendMessageToMember(this.systemMessageUser?.id);
        this.setMessage("");
        const noticeBlock = `<h6><strong>${notice?.title}</strong></h6> <div>${
          notice?.start_date
            ? format(new Date(notice?.start_date), "MM/dd/yyyy")
            : ""
        }</div> <div>${notice?.body}</div>`;

        await this.sendMessage(noticeBlock);
      }

      await api.messages.post(
        {
          notifications: [notice?.id],
        },
        `my-notifications/archive/`
      );
      this.newsAndPolicies = this.newsAndPolicies.filter(
        item => !notice?.id.includes(item?.id)
      );

      this.isDeleting = false;
      this.readConversation(null);

      const { data: unreadCountData } = await api.messages.get(
        `my-notifications/unread-count/`
      );
      const unreadCount = unreadCountData?.count;

      if (unreadCount !== undefined) {
        this.unreadNewsAndPolicyCount = unreadCount;
      }

      snackbarStore.enqueueSnackbar({
        message: {
          id: "messages.success.delete-conversation",
          default: "Removed this conversation successfully",
        },
        variant: SnackbarVariants.SUCCESS,
      });
      this.log("Message Center: Successfully deleted notice");
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: "messages.error.delete-conversation",
          default: "There was an error while deleting conversation",
        },
        variant: SnackbarVariants.ERROR,
      });
      this.log("Message Center: Error deleting notice", error);
    }
  };

  deleteConversations = async () => {
    window["deleteConversations"] = this.deleteConversations;
    const isDeletingMultiple =
      this.selectedConversations && this.selectedConversations.length > 1;
    const isDeletingActiveConversation =
      this.selectedConversations && this.selectedConversations.length <= 0;

    try {
      let conversationsToDelete = isDeletingActiveConversation
        ? [this.activeConversationId]
        : this.selectedConversations;

      // if active conversation is a draft, just remove it from the list of conversations
      // no need to delete
      const activeConversation = this.conversations.find(
        conversation => conversation.id === this.activeConversationId
      );
      if (
        conversationsToDelete.includes(this.activeConversationId) &&
        activeConversation?.is_draft
      ) {
        conversationsToDelete = conversationsToDelete.filter(
          conversationId => conversationId !== this.activeConversationId
        );
      }

      if (conversationsToDelete.length > 0) {
        await api.messages.post(
          {
            conversations: conversationsToDelete,
          },
          `conversations/archive/`
        );
        this.conversations = this.conversations.filter(
          conversation => !conversationsToDelete.includes(conversation.id)
        );
      }

      this.setSelectedConversations([]);

      this.isDeleting = false;
      this.readConversation(null);
      this.conversations = this.conversations.filter(
        conversation => conversation.id !== this.activeConversationId
      );
      snackbarStore.enqueueSnackbar({
        message: {
          id: isDeletingMultiple
            ? "messages.success.delete-conversations"
            : "messages.success.delete-conversation",
          default: isDeletingMultiple
            ? "Removed {count} conversations successfully"
            : "Removed this conversation successfully",
          parameters: {
            count: conversationsToDelete.length,
          },
        },
        variant: SnackbarVariants.SUCCESS,
      });
    } catch (error) {
      snackbarStore.enqueueSnackbar({
        message: {
          id: isDeletingMultiple
            ? "messages.error.delete-conversations"
            : "messages.error.delete-conversation",
          default: isDeletingMultiple
            ? "There was an error while deleting conversations"
            : "There was an error while deleting conversation",
        },
        variant: SnackbarVariants.ERROR,
      });
      this.log("Message Center: Error deleting conversations", error);
    }
  };

  selectContacts = (
    contact: MessageCenterContactDetails,
    multiple: boolean = false
  ) => {
    if (!this.selectedContacts) {
      this.setSelectedContacts([]);
    }
    if (this.selectedContacts) {
      const contactId = contact?.id;
      const idIndex = this.selectedContacts.indexOf(contactId).toString();
      if (multiple) {
        if (idIndex === "-1") {
          this.setSelectedContacts([...this.selectedContacts, contactId]);
        } else {
          const filteredContacts = this.selectedContacts.filter(
            id => id !== contactId
          );
          this.setSelectedContacts(filteredContacts);
        }
      } else {
        this.setSelectedContacts([contactId]);
      }
    }
  };

  unSelectAllContacts = () => {
    this.setSelectedContacts([]);
  };

  selectAllContacts = () => {
    if (this.isBulkMessageView) {
      this.contacts.forEach(
        contact =>
          this.selectedContacts.indexOf(contact?.id) < 0 &&
          contact.can_receive_bulk_message &&
          this.selectContacts(contact, true)
      );
    } else {
      this.contacts.forEach(
        contact =>
          this.selectedContacts.indexOf(contact?.id) < 0 &&
          this.selectContacts(contact, true)
      );
    }
  };

  setSelectedContacts = (contacts: string[]) => {
    this.selectedContacts = contacts;
  };

  setIsComposing = (composing: boolean) => {
    this.isComposing = composing;

    if (composing) {
      this.getContacts();
    }
  };

  setIsSearchingView = (searching: boolean) => {
    this.isSearchingView = searching;
  };

  setIsBulkMessageView = (is_bulk_view: boolean) => {
    this.isBulkMessageView = is_bulk_view;
  };

  selectConversations = (conversation: MessageCenterConversationDetails) => {
    if (this.selectedConversations) {
      const conversationId = conversation?.id;
      const idIndex = this.selectedConversations
        .indexOf(conversationId)
        .toString();
      if (idIndex === "-1") {
        this.setSelectedConversations([
          ...this.selectedConversations,
          conversationId,
        ]);
      } else {
        const filteredConversations = this.selectedConversations.filter(
          id => id !== conversationId
        );
        this.setSelectedConversations(filteredConversations);
      }
    }
  };

  selectAllConversations = () => {
    if (this.selectedConversations) {
      this.unSelectAllConversations();
      this.conversations.forEach(conversation => {
        this.selectConversations(conversation);
      });
    }
  };

  unSelectAllConversations = () => {
    this.setSelectedConversations([]);
  };

  setSelectedConversations = (conversations: string[]) => {
    this.selectedConversations = conversations;
  };

  resetCrucialVariblesState = () => {
    const variables: Array<keyof MessageStore> = [
      "isDeleting",
      "isComposing",
      "isSearchingView",
      "message",

      "selectedContacts",
      "nextContactsUrl",
      "isContactsLoading",
      "isContactsLoadingMore",
      "isSearchingContacts",
      "contactsSearchTerm",

      "selectedConversations",
      "activeConversation",
      "activeConversationMessages",
      "nextConversationsUrl",
      "isConversationsLoading",
      "isConversationsLoadingMore",
      "conversationsSearchTerm",
      "isSearchingConversations",

      "nextMessagesUrl",
      "isMessagesLoading",
      "isMessagesLoadingMore",

      "requestTimeout",
    ];

    variables.forEach((variable: any) => {
      const instanceVar = this[variable];
      const initialDataVar = InitialData[variable];

      if (
        typeof instanceVar !== "undefined" &&
        (typeof initialDataVar !== "undefined" || initialDataVar === undefined)
      ) {
        this[variable] = initialDataVar;
      }
    });
  };

  public signAttachments = async (name: string, fileSize?: number) => {
    const { data } = await api.messages.post(
      {
        file_name: name,
        file_size: fileSize,
      },
      "conversations/sign-message-attachments/"
    );
    const { key, presigned_post, view_url } = data;

    return { view_url, presigned_post, key };
  };

  public transcodeVideo = async (
    key: string,
    file: File,
    uploadProgressCallback: (progressEvent) => void,
    conversationId: string
  ) => {
    this.log("Message Center: transcodeVideo start", file);

    this.transcodingCancelTokenSource.cancel();
    this.transcodingCancelTokenSource = cancelToken.source();

    const uploadData = createCustomFormData({
      conversation_id: conversationId,
      files: [file],
    });

    const { data } = await api.attachmentUploader.post(
      uploadData,
      "/attachment-upload/",
      {},
      {
        cancelToken: this.transcodingCancelTokenSource.token,
        onUploadProgress: uploadProgressCallback,
      }
    );

    const attachmentData: Array<MessageVideoAttachmentUploadedData> =
      Object.values(data);
    const target = attachmentData.find(
      attachment => attachment.attachment_key === key
    ) ||
      data[file.name] ||
      data[file.name.toLowerCase()] ||
      data[Object.keys(data)?.[0]] || {
        length: 0,
        attachment_key: "",
        attachment_url: "",
        thumbnail_key: "",
        thumbnail_url: "",
      };

    const {
      length,
      attachment_key,
      attachment_url,
      thumbnail_key,
      thumbnail_url,
    } = target;

    return {
      length,
      attachment_key,
      attachment_url,
      thumbnail_key,
      thumbnail_url,
    };
  };

  public uploadAttachments = async (
    messageId: string,
    key: string,
    image: File,
    presigned_post: {
      form_data: Object;
      post_url: string;
    },
    attachment: MessageCenterMessageAttachmentDetails
  ) => {
    const conversationId = this.activeConversationId;

    if (!conversationId) {
      this.log(
        "Message Center: uploadAttachments aborted since there is no active conversation"
      );
      return;
    }

    const tempAttachmentsPendingUpload = this.attachmentsPendingUpload;
    let cancelUpload = () => {};
    const attachmentData: PendingAttachmentData = {
      message_id: messageId,
      key,
      image,
      presigned_post,
      progress:
        attachment?.processing_data?.loaded ===
        attachment?.processing_data?.total
          ? 100
          : 0,
      cancelToken: new cancelToken(function executor(c) {
        cancelUpload = c;
      }),
      cancel: cancelUpload,
      attachment_data: attachment,
    };
    const pendingAttachmentsLength =
      tempAttachmentsPendingUpload.push(attachmentData);
    const attachmentIndex = pendingAttachmentsLength - 1;
    const uploadData = createFormData(image, presigned_post.form_data);
    this.attachmentsPendingUpload = [...tempAttachmentsPendingUpload];

    if (!attachmentData.progress) {
      await rawAxios.post(presigned_post.post_url, uploadData, {
        cancelToken: this.attachmentsPendingUpload[attachmentIndex].cancelToken,
        onUploadProgress: progressEvent => {
          this.handleAttachmentUploadProgress(progressEvent, attachmentIndex);
        },
      });
    }

    this.handleAttachmentUploadProgress(
      {
        loaded: image.size,
        total: image.size,
      },
      attachmentIndex,
      true
    );

    const morePendingUploads = !!this.attachmentsPendingUpload.filter(
      attachment =>
        attachment.message_id === messageId && attachment.progress !== 100
    ).length;
    if (!morePendingUploads) {
      this.handleAttachmentUploadComplete(
        this.attachmentsPendingUpload[attachmentIndex]
      );
    }
  };

  private handleAttachmentUploadProgress = (
    progressEvent,
    attachmentIndex,
    markComplete?: boolean
  ) => {
    const tempAttachmentsPendingUpload = this.attachmentsPendingUpload;
    tempAttachmentsPendingUpload[attachmentIndex].progress = markComplete
      ? 100
      : Math.min(
          Math.round(100 * (progressEvent.loaded / progressEvent.total)),
          99
        );
    tempAttachmentsPendingUpload[
      attachmentIndex
    ].attachment_data.processing_data = {
      loaded: progressEvent.loaded,
      total: progressEvent.total,
    };
    this.attachmentsPendingUpload = [...tempAttachmentsPendingUpload];
  };

  public cancelPendingAttachments = (message_id: string) => {
    const attachmentsToDelete: Array<string> = [];

    this.attachmentsPendingUpload.forEach(attachment => {
      if (attachment.message_id === message_id) {
        attachment.cancel?.();

        if (attachment.progress === 100) {
          attachmentsToDelete.push(attachment.key);
        }
      }
    });

    if (attachmentsToDelete.length) {
      api.messages
        .post(
          {
            attachment_keys: [...attachmentsToDelete],
          },
          `conversations/delete-uploaded-file/`
        )
        .catch(reason => {
          this.log("Message Center: failed to delete uploaded file", reason);
        });
    }

    this.removePendingAttachments(message_id);
  };

  private removePendingAttachments = (message_id: string) => {
    this.attachmentsPendingUpload = [
      ...this.attachmentsPendingUpload.filter(
        current_attachment => current_attachment.message_id !== message_id
      ),
    ];
  };

  private handleAttachmentUploadComplete = async (
    attachment: PendingAttachmentData
  ) => {
    const pendingMessageIndex = this.sendMessageQueue.findIndex(
      message => message.message_id === attachment.message_id
    );
    const pendingAttachments = this.attachmentsPendingUpload.filter(
      current_attachment =>
        current_attachment.message_id === attachment.message_id
    );
    const pendingAttachmentsComplete = pendingAttachments.every(
      item => item.progress === 100
    );

    if (pendingMessageIndex >= 0 && pendingAttachmentsComplete) {
      const tempSendMessageQueue = this.sendMessageQueue;
      const sendRequest = tempSendMessageQueue.splice(
        pendingMessageIndex,
        1
      )[0] as PendingMessage;
      this.sendMessageQueue = [...tempSendMessageQueue];

      this.removePendingAttachments(attachment.message_id);

      this.processQueuedSendMessage(
        sendRequest.conversation_id,
        sendRequest.request_data,
        sendRequest.send_endpoint,
        sendRequest.is_bulk_message,
        () => {},
        MESSAGE_LIST_REFRESH_DELAY_IN_MS
      );
    } else if (pendingMessageIndex < 0) {
      setTimeout(() => {
        this.refreshActiveConversationMessages();
      }, MESSAGE_LIST_REFRESH_DELAY_IN_MS);
    }
  };

  public setMessageFontSize = (fontSize: TFontSize) => {
    this.messageFontSize = fontSize;
  };

  public setMessageInputFocused = (isFocused: boolean) => {
    this.messageInputFocused = isFocused;
  };

  public setAttachImagesToBeSent = (imgs: string[]) => {
    this.attachedImagesToBeSent = imgs;
  };

  public setimageKeysTobeSubmitted = (imgKeys: string[]) => {
    this.imageKeysTobeSubmitted = imgKeys;
  };

  public setAttachmentsTobeSubmitted = (
    attachments: MessageAttachmentDetails[]
  ) => {
    this.attachmentsTobeSubmitted = attachments;
  };

  public setCanCurrentMemberReceiveMsg = (canReceiveMsg: boolean) => {
    this.canCurrentMemberReceiveMsg = canReceiveMsg;
  };
  public setTempCanMemberReceiveMsg = (canMemeberIDReceiveMsg: string) => {
    this.canTempMemberReceiveMsg.push(canMemeberIDReceiveMsg);
  };

  public logArchivedMsgVisit = async () => {
    await axios.post(`logging/track-archived-messages-clicks/`);
  };

  public logMsgCenterVisit = async () => {
    if (this.isOnMessagesPage || this.isMessagesMenuInNavbarOpen) {
      await axios.post(`logging/track-message-center-visit/`);
    }
  };

  public handleNewMessageReceived = async data => {
    // don't update messages if broadcaster is open, can be removed once new model center as a whole is deployed
    try {
      this.log("Message Center: handleNewMessageReceived start", data);
      if (!stores?.layoutStore?.isBroadcastPage) {
        await this.getUnreadConversations();
        if (data?.action === "RESOLVED" || data?.action === "UNRESOLVED") {
          await this.getConversations(false, true);
          this.activeConversation =
            this.conversations.find(
              convo => convo?.id === this.activeConversationId
            ) || this.activeConversation;
        }
        if (
          this.activeConversation?.participants?.find?.(
            participant => participant?.id === data?.sender?.id
          )
        ) {
          // refresh messages if sender is from the active conversation
          this.refreshActiveConversationMessages();
        }
      }
    } catch (error) {
      this.log("Message Center: handleNewMessageReceived fail", error);

      throw error;
    } finally {
      this.log("Message Center: handleNewMessageReceived finished");
    }
  };

  public handleMessageRejection = data => {
    try {
      const message = data as MessageCenterMessageDetails;
      this.log("Message Center: handleMessageRejection start", message);

      const target_message = this.activeConversationMessages?.find(
        msg => msg?.id === message?.id
      );
      if (!!target_message) {
        this.refreshActiveConversationMessages();
      }
    } catch (error) {
      this.log("Message Center: handleMessageRejection fail", error);

      throw error;
    } finally {
      this.log("Message Center: handleMessageRejection finished");
    }
  };

  public handleMessagePurchased = data => {
    try {
      this.log("Message Center: handleMessagePurchased start", data);

      const result: MessageCenterMessageDetails[] = this
        .activeConversationMessages
        ? this.activeConversationMessages.map(msg => {
            if (msg?.id === data?.message_id) {
              return {
                ...msg,
                attachments_data: msg.attachments_data
                  ? msg.attachments_data.map(media => {
                      if (media?.id === data?.attachment_id) {
                        return { ...media, is_purchased: true };
                      } else {
                        return { ...media };
                      }
                    })
                  : [],
              };
            }

            return msg;
          })
        : [];

      this.activeConversationMessages = result;
    } catch (error) {
      this.log("Message Center: handleMessagePurchased fail", error);

      throw error;
    } finally {
      this.log("Message Center: handleMessagePurchased finished");
    }
  };

  get isMCPhase2Active() {
    // const mcPhase2Cookie = CookieStorage.get("MCPhase2");

    // return !!mcPhase2Cookie;
    return true;
  }

  get isMCPhase2MediaActive() {
    const mcPhase2MediaCookie = CookieStorage.get("MCPhase2Media");

    return !!mcPhase2MediaCookie;
  }

  get isCSAgentChatActive() {
    // return authStore.isFeatureEnabled(FEATURE_FLAGS.FEATURE_NEW_MC_CS_AGENT_CHAT);
    // CDP-11167 - disable CS Agent Chat to migrate to influencer
    return false;
  }

  clearConversationAttachments = () => {
    this.activeConversationAttachmentsCount = 0;
    this.activeConversationAttachments =
      InitialData.activeConversationAttachments;
    this.nextAttachmentsUrl = InitialData.nextAttachmentsUrl;
  };

  setActiveMCSharedMediaModalTab = (tab: SharedMediaTab) => {
    this.activeMCSharedMediaModalTab = tab;
  };

  getConversationAttachments = async () => {
    try {
      this.log("getConversationAttachments start");
      if (
        this.activeConversationId &&
        !this.activeConversationAttachmentsLoading
      ) {
        this.activeConversationAttachmentsLoading = true;
        const purchaseFilter =
          this.activeMCSharedMediaModalTab === "for-sale"
            ? `is_purchased=false&sender=self`
            : this.activeMCSharedMediaModalTab === "sold"
            ? `is_purchased=true&sender=self`
            : "";
        //note: BE endpoint also exposes attachment_type=image/video/voice filter.
        const limit = ATTACHMENTS_LIST_PAGE_SIZE;
        const url = this.nextAttachmentsUrl
          ? this.nextAttachmentsUrl.split("notifications/")[1]
          : `conversations/${
              this.activeConversationId
            }/all-attachments/?limit=${limit}&ordering=-created_at${
              purchaseFilter ? `&${purchaseFilter}` : ""
            }`;
        const { data } = await api.messages.get(url);
        this.activeConversationAttachments = [
          ...this.activeConversationAttachments,
          ...data?.results,
        ];
        this.activeConversationAttachmentsCount = data?.count || 0;
        this.log("getConversationAttachments: success", data);

        this.nextAttachmentsUrl = data?.next;
      } else {
        this.log("getConversationAttachments: Active conversation is missing");
      }
    } catch (error) {
      this.log(
        "getConversationAttachments: Encountered an error while fetching attachments",
        error
      );
    } finally {
      this.activeConversationAttachmentsLoading = false;
    }
  };
}
